// v2.22 file master: the browser side of the multipart upload (docs/rencana/FILE-MASTER-R2.md bagian 1).
// The file goes straight from the browser to Cloudflare R2 in 10 MiB parts through short presigned URLs;
// the server only starts, lists and joins. Everything the flow needs from outside is passed in (`api`,
// `kirimBagian`, `tidur`), so Jest tests it without a network. Resume = choose the same file again: the
// server recognises it by SHA-256 + size, lists the parts already in the bucket, and only the rest is sent.
import { Sha256 } from "./sha256";
import { sisaBagian, rentangBagian, jedaUlang } from "./fileMaster";

const BACA_SIDIK = 8 * 1024 * 1024; // bytes read at a time while hashing
const MAKS_TAUTAN = 20; // part URLs per request (server limit)
const ULANG = 3; // retries per part after the first try
const SISA_BERLAKU_MS = 60 * 1000; // ask for a new URL when less than this is left

export class Dihentikan extends Error {
  constructor() { super("Unggahan dijeda"); this.dijeda = true; }
}

const cekBerhenti = (sinyal) => { if (sinyal?.aborted) throw new Dihentikan(); };

export async function sidikFile(file, { saatProgres, sinyal } = {}) {
  const h = new Sha256();
  for (let a = 0; a < file.size; a += BACA_SIDIK) {
    cekBerhenti(sinyal);
    const b = Math.min(file.size, a + BACA_SIDIK);
    h.update(new Uint8Array(await file.slice(a, b).arrayBuffer()));
    saatProgres?.({ tahap: "sidik", byte: b, total: file.size });
  }
  return h.hex();
}

// api: {mulai(spkId, body), tautan(fid, nomor[]), bagian(fid), selesai(fid, bagian[])} (promises of response data)
// kirimBagian(url, blob, saatTerkirim(byte), sinyal) -> Promise<etag>; a failure carries .status (HTTP) when known
// sinyal (AbortSignal) = stop now, parts in flight are lost. jeda ({diminta}) = the Jeda button: no new part
// is started, the parts in flight finish first (R2 keeps only whole parts: with 3 parts of 10 MiB in flight,
// stopping at once could throw away up to 30 MiB — measured 2026-10-29, nothing complete at 30–50% of 60 MB).
export async function unggahFileMaster({ file, spkId, api, kirimBagian, saatProgres, sinyal, jeda, sha256,
  tidur = (ms) => new Promise((r) => setTimeout(r, ms)), kini = () => Date.now() }) {
  const sidik = sha256 || await sidikFile(file, { saatProgres, sinyal });
  cekBerhenti(sinyal);
  const awal = await api.mulai(spkId, { nama_file: file.name, ukuran_byte: file.size, sha256: sidik });
  const fid = awal.file.id;
  const { ukuran_bagian: ukuranBagian, jumlah_bagian: jumlah } = awal;
  const selesai = new Map(); // nomor -> etag
  if (awal.lanjut) {
    for (const b of (await api.bagian(fid)).bagian || []) {
      const [x, y] = rentangBagian(b.nomor, ukuranBagian, file.size);
      if (b.ukuran === y - x && b.etag) selesai.set(b.nomor, b.etag); // a part of the wrong size is sent again
    }
  }
  const antre = sisaBagian(jumlah, [...selesai.keys()].map((nomor) => ({ nomor })));
  const terkirimAwal = [...selesai.keys()].reduce((s, n) => { const [x, y] = rentangBagian(n, ukuranBagian, file.size); return s + y - x; }, 0);
  const berjalan = new Map(); // nomor -> bytes of that part sent so far
  const mulaiMs = kini();
  let byteSesiIni = 0;
  const lapor = () => {
    let jalan = 0;
    for (const v of berjalan.values()) jalan += v;
    saatProgres?.({ tahap: "unggah", byte: terkirimAwal + byteSesiIni + jalan, total: file.size,
      byteSesi: byteSesiIni + jalan, msSesi: kini() - mulaiMs, bagianSelesai: selesai.size, jumlahBagian: jumlah,
      lanjut: !!awal.lanjut, fid, versi: awal.file.versi });
  };
  lapor();

  const tautan = new Map(); // nomor -> {url, sampai}
  const ambilTautan = async (nomor, paksa) => {
    const t = tautan.get(nomor);
    if (!paksa && t && t.sampai - kini() > SISA_BERLAKU_MS) return t.url;
    const minta = [nomor, ...antre.filter((n) => n !== nomor && !tautan.has(n))].slice(0, MAKS_TAUTAN);
    const r = await api.tautan(fid, minta);
    const sampai = kini() + (r.berlaku_detik || 600) * 1000;
    for (const b of r.bagian) tautan.set(b.nomor, { url: b.url, sampai });
    return tautan.get(nomor).url;
  };

  const kirimSatu = async (nomor) => {
    const [x, y] = rentangBagian(nomor, ukuranBagian, file.size);
    let paksaTautan = false;
    for (let coba = 0; ; coba++) {
      cekBerhenti(sinyal);
      if (coba > 0 && jeda?.diminta) throw new Dihentikan(); // a retry after Jeda waits for Lanjut
      try {
        const url = await ambilTautan(nomor, paksaTautan);
        const etag = await kirimBagian(url, file.slice(x, y), (n) => { berjalan.set(nomor, n); lapor(); }, sinyal);
        if (!etag) throw Object.assign(new Error("ETag tidak terbaca"), { status: 0 });
        berjalan.delete(nomor);
        byteSesiIni += y - x;
        selesai.set(nomor, etag);
        lapor();
        return;
      } catch (e) {
        berjalan.delete(nomor);
        if (e instanceof Dihentikan || sinyal?.aborted) throw new Dihentikan();
        if (e.response) throw e; // an answer of the SOQPRINT server itself (403/404/409 …): not a network hiccup
        if (coba >= ULANG) throw Object.assign(new Error(`Bagian ${nomor} gagal terkirim setelah ${ULANG + 1} kali mencoba. Periksa internet, lalu pilih file yang sama untuk melanjutkan.`), { bagian: nomor });
        paksaTautan = e.status === 403 || e.status === 400; // expired or refused link: ask for a new one
        await tidur(jedaUlang(coba + 1));
      }
    }
  };

  const pekerja = Math.max(1, Math.min(awal.bagian_bersamaan || 3, antre.length || 1));
  let galat = null;
  const ambilBerikut = () => (jeda?.diminta ? undefined : antre.shift());
  await Promise.all(Array.from({ length: pekerja }, async () => {
    for (let n = ambilBerikut(); n !== undefined && !galat; n = ambilBerikut()) {
      try { await kirimSatu(n); } catch (e) { galat = galat || e; }
    }
  }));
  if (galat) throw galat;
  if (selesai.size < jumlah) throw new Dihentikan(); // paused with parts left
  cekBerhenti(sinyal);
  saatProgres?.({ tahap: "satukan", byte: file.size, total: file.size, fid, versi: awal.file.versi });
  const bagian = [...selesai.entries()].sort((a, b) => a[0] - b[0]).map(([nomor, etag]) => ({ nomor, etag }));
  return { file: await api.selesai(fid, bagian), sha256: sidik, lanjut: !!awal.lanjut };
}

// The real part sender: XMLHttpRequest (fetch has no upload progress). No cookies are sent to R2.
export function kirimBagianXhr(url, blob, saatTerkirim, sinyal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const henti = () => xhr.abort();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => saatTerkirim?.(e.loaded);
    xhr.onload = () => {
      sinyal?.removeEventListener("abort", henti);
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader("ETag"));
      else reject(Object.assign(new Error(`HTTP ${xhr.status}`), { status: xhr.status }));
    };
    xhr.onerror = () => { sinyal?.removeEventListener("abort", henti); reject(Object.assign(new Error("Jaringan putus"), { status: 0 })); };
    xhr.onabort = () => { sinyal?.removeEventListener("abort", henti); reject(new Dihentikan()); };
    sinyal?.addEventListener("abort", henti);
    xhr.send(blob);
  });
}
